import {fabric} from "fabric"
import JSZip from "jszip"
import {storage} from "../firebase"
import {v4 as uuidv4} from "uuid"
import * as CRYPTO_JS from "crypto-js"
import {changeDpiDataUrl} from "changedpi"
import { getImageAndFrame, processSvg } from "./viewer2d.helpers"

/**
 *Class for working with canvas,
 *includes all methods for working with text,
 *images and other objects on the canvas
 * @class
 * @param  {object} props objects with all propertys for work
 * @param  {object} props.node dom element
 * @param  {function} props.setSpinner preloader function
 * @param  {string} props.key string for zip saving
 * @param  {object} props.options additional options
 * @example
 * const props = {
 *          node: domElement,
 *          setSpinner: ()=>{},
 *          key: '',
 *          options: {},
 *        }
 * new Viewer2d(props)
 */
class Viewer2d {
  constructor(props) {
    this.props = props

    if (fabric.isWebglSupported()) fabric.textureSize = 65536;

    this._init()
  }

  _linesDistanceConst = 2
  _defVpTransform = [1, 0, 0, 1, 0, 0]
  _zoomConst = 1

  disabledObject = {
    centeredRotation: false,
    centeredScaling: false,
    evented: false,
    hasBorders: false,
    hasControls: false,
    hasRotatingPoint: false,
    lockRotation: true,
    lockMovementY: true,
    lockMovementX: true,
    selectable: false,
    hoverCursor: "default",
  }

  help = {
    selectedObj: () => {},
    activeObj: () => {},
    activeObjs: () => {},

    addToCartDisabled: () => {},
  }

  canvasContent = []
  canvasConfigs = []

  /**
   * create id for object
   * @returns {string} id - fabricjs object
   * @method
   */
  _crtId = () =>
    String.fromCharCode(65 + Math.floor(Math.random() * 26)) + Date.now()

  /**
   * create clip path
   * @param {object} obj - fabricjs object
   * @returns {object} clip - fabricjs object
   * @method
   */
  _crtClip = obj => {
    const clip = new fabric.Rect({
      width: obj.width - obj.strokeWidth / 2,
      height: obj.height - obj.strokeWidth / 2,
      left: obj.left + obj.strokeWidth / 2,
      top: obj.top + obj.strokeWidth / 2,

      fill: "transparent",
      absolutePositioned: true,
      ...this.disabledObject,
    })

    return clip
  }

  /**
   * viewer init method
   * @method
   */
  _init = () => {
    const {node} = this.props
    this.c = new fabric.Canvas(node, {
      preserveObjectStacking: true,
    })

    this.c.on("mouse:up", this._upHandler)
    this.c.on("mouse:down", this._downHandler)
    this.c.on("after:render", () => {})
    this.c.on("object:modified", this._handleModified)
    this.c.on("selection:created", this._handleSelected)
    this.c.on("selection:cleared", e => {
      e.deselected &&
        e.deselected.forEach(
          item => (item.help.left = this.c.width / 2 - item.left),
        )
    })
    this.c.on("object:moving", this._handleMoving)
  }

  /**
   * hendler for for mouse up event
   * @method
   */
  _upHandler = e => {
    let acObjs = this.c.getActiveObjects()

    if (e.target) {
      if (!e.target.help) {
        return
      }

      e.target.help.left = this.c.width / 2 - e.target.left
      e.target.help.top = this.c.height / 2 - e.target.top
    }

    this.help.selectedObj(e)
    this.help.activeObj(e)
    this.help.activeObjs(acObjs)
    this.c.bringToFront(this.c.getObjects().find(item => item.name === "lines"))

    if (e.target) {
      let lines = this.c.getObjects().find(item => item.name === "lines")
      ;[lines.item(0), lines.item(1)].forEach(item =>
        item.set({visible: true}),                  /// TESTING needed - was false earlier
      )

      // e.target.setCoords()
      // this.c.renderAll()
    }
  }

  /**
   * hendler for for mouse down event
   * @method
   */
  _downHandler = e => {}

  /**
   * hendler for modified fabricjs object event
   * @method
   */
  _handleModified = e => {
    this.help.activeObj(e)
  }

  /**
   * hendler for moving fabricjs object event
   * @method
   */
  _handleMoving = e => {
    let lines = this.c.getObjects().find(item => item.name === "lines")

    let targetCenter = {
      x: e.target.top + (e.target.height * e.target.scaleY) / 2,
      y: e.target.left + (e.target.width * e.target.scaleX) / 2,
    }

    let linesCenter = {
      x: lines.top + lines.height / 2,
      y: lines.left + lines.width / 2,
    }

    let blockDitsance = 2
    let blockTime = 150

    if (
      Math.round(targetCenter.x) < Math.round(linesCenter.x) + blockDitsance &&
      Math.round(targetCenter.x) > Math.round(linesCenter.x) - blockDitsance
    ) {
      // console.log("BLOCK MOVEMENT Y")
      lines.item(0).set({visible: true})
      e.target.lockMovementY = true
      e.target.top = linesCenter.x - (e.target.height * e.target.scaleY) / 2
    } else {
      lines.item(0).set({visible: false})
    }

    if (
      Math.round(targetCenter.y) < Math.round(linesCenter.y) + blockDitsance &&
      Math.round(targetCenter.y) > Math.round(linesCenter.y) - blockDitsance
    ) {
      // console.log("BLOCK MOVEMENT X")
      lines.item(1).set({visible: true})
      e.target.lockMovementX = true
      e.target.left = linesCenter.y - (e.target.width * e.target.scaleX) / 2
    } else {
      lines.item(1).set({visible: false})
    }

    if (e.target.lockMovementX === true || e.target.lockMovementY === true) {
      setTimeout(() => {
        e.target.lockMovementX = false
        e.target.lockMovementY = false
      }, blockTime)
    }

    this.c.renderAll()
  }

  /**
   * add product to scene / create box for product / create center lines
   * @param {string} img - base64 image
   * @param {string} name - part name
   * @param {object} config - config from firebase
   * @method
   */
  addProduct = (img, name, config, color) => {
    //Guy
    if (config.dx === undefined) {
      config.dx = -1;
      config.description = "...";
      config.image="..";
      config.object_id = "TSHIRT_KID_10_FRONT";
      config.index = 1;
      config.wNetInUOM = -1;
      config.height=28.5;
      config.name = 'Tshirt Kid 10 Front';
      config.hNetInUOM = 58;
      config.width = 36.1;
      config.dy = 6.5;
      config.ygap = 147;
      config.wnet = 283;
      config.xgap = -1;
      config.hnet = 637;
    }
    //Guy
    console.log("config by: ", config)

    if (!this.canvasConfigs.find(i => i.added_name === name)) {
      this.canvasConfigs.push({...config, added_name: name})
    }

    const getBorderSizesAndPositions = image => {
      let pxInCm
      let sizesAndPosition = {
        left: 0,
        top: 0,
        width: 0,
        height: 0,
      }

      let findPx = {
        wpx: config.wNetInUOM > -1 ? config.wnet / config.wNetInUOM : -1,
        hpx: config.hNetInUOM > -1 ? config.hnet / config.hNetInUOM : -1,
      }

      let max = Math.max(findPx.wpx, findPx.hpx)

      if (max > 0) {
        pxInCm = max
      } else {
        pxInCm = 8
      }

      let wNetInCm = parseFloat((config.wnet / pxInCm).toFixed(2))

      let maxWidth

      if (config.width > wNetInCm) {
        maxWidth = wNetInCm
      } else {
        maxWidth = config.width
      }

      console.log("wnet in Cm", wNetInCm)
      console.log("border width in cm", config.width*this._zoomConst)

      // this._linesDistanceConst = centerLineActiveDistanceCm * pxInCm
      sizesAndPosition = {
        ...sizesAndPosition,
        width: config.width ? config.width : image.width / 2,
        height: config.height ? config.height: image.height / 2,
      }

      let positionX = () => {
        if (config.dx) {
          if (parseFloat(config.dx) === -1) {
            return {
              left: image.left + image.width / 2 - sizesAndPosition.width / 2,
            }
          } else {
            return {
              left: parseFloat(config.dx) + image.left,
            }
          }
        } else {
          console.error("ERROR: position in config is not valid")
          return {
            left: image.left + image.width / 2 - sizesAndPosition.width / 2,
          }
        }
      }

      let positionY = () => {
        if (config.dy) {
          if (parseFloat(config.dy) === -1) {
            return {
              top: image.top + image.height / 2 - sizesAndPosition.height / 2,
            }
          } else {
            return {
              top: parseFloat(config.dy) + image.top,
            }
          }
        } else {
          console.error("ERROR: position in config is not valid")
          return {
            top: image.top + image.height / 2 - sizesAndPosition.height / 2,
          }
        }
      }

      return (sizesAndPosition = {
        ...sizesAndPosition,
        ...positionX(),
        ...positionY(),
      })
    }

    this.c.stateName = name

    processSvg(fabric, img, (extractedImage, frame1, frame2, shadowImages) => {
      const { data, width, height } = extractedImage||{}

      if(!extractedImage){
        this.props.setSpinner(false);
        return
      }

      this.props.setSpinner(true);

      config.width = frame1.width;
      config.height = frame1.height;
      config.dx= frame1.left;
      config.dy= frame1.top;

      const promises = [];

      shadowImages.forEach(({ data, width, height, top, left }) => {
        promises.push(new Promise((resolve, reject) => {
          fabric.Image.fromURL(data, image => {
            image.set({
              width, height, top, left, originX: "left",
              originY: "top",
            })
            resolve(image)
          })
        }))
      })

      Promise.all(promises).then((result) => {
    fabric.Image.fromURL(
        data,
      image => {
        image.set({
              // =====================very small image
              width: width < 600 ? 600 : width,
              height: height < (height / width) * 600 ? 600 : height,
              name: 'garment'
            })

            let imageLayers = new fabric.Group([image, ...result].reverse())

            imageLayers.set({
          ...this.disabledObject,
          originX: "left",
          originY: "top",
          name: "product",
        })

        if (this.c.height > 800) {
              const desiredWidth = this.c.width < 550 ? this.c.width : 550;
              this._zoomConst = desiredWidth / width
        } else {
              const desiredWidth = this.c.width < 450 ? this.c.width : 450;
              this._zoomConst = (desiredWidth) / width
        }

        this.canvasContent.push({name: name, payload: []})
            this.c.centerObject(imageLayers)
  
        this._zoom(this._zoomConst)

            imageLayers.zoom = this._zoomConst

          
        const shadow = {
          color: "white",
          blur: 1.1,
          offsetX: 0.5,
          offsetY: 0.5,
          opacity: 1,
        }

        const prodRect = new fabric.Rect({
          fill: "transparent",
          stroke: "#111",
          strokeWidth: 1,
          strokeDashOffset: 1,
          name: "product-border",
          shadow: shadow,

              ...getBorderSizesAndPositions(imageLayers),
          ...this.disabledObject,
          visible:false
        })

        prodRect.help = {
          left: this.c.width / 2 - prodRect.left,
          top: this.c.height / 2 - prodRect.top,
        }

        const rect1 = new fabric.Rect({
          fill: "transparent",
          stroke: frame1.stroke,//"black",
          strokeWidth: frame1.strokeWidth,
          strokeDashOffset: 1,
          strokeDashArray: frame1.strokeDashArray,
          strokeMiterLimit: frame1.strokeMiterLimit,
          name: "product-border1",
          // shadow: shadow,

          ...getBorderSizesAndPositions(image),
          ...this.disabledObject,
        })

        rect1.help = {
          left: this.c.width / 2 - rect1.left,
          top: this.c.height / 2 - rect1.top,
        }

        const rect2 = new fabric.Rect({
          fill: "transparent",
          stroke: frame2.stroke,//"white",
          strokeWidth: frame2.strokeWidth,
          strokeDashOffset: 1,
          strokeDashArray: frame2.strokeDashArray,
          strokeMiterLimit: frame2.strokeMiterLimit,
          name: "product-border2",

          ...getBorderSizesAndPositions(image),
          ...this.disabledObject,
        })

        rect2.help = {
          left: this.c.width / 2 - rect2.left,
          top: this.c.height / 2 - rect2.top,
        }

        rect1.zoom = this._zoomConst
        rect2.zoom = this._zoomConst

        let group = new fabric.Group([rect1, rect2])
        group.set({
          width: prodRect.width + prodRect.strokeWidth / 2,
          height: prodRect.height + prodRect.strokeWidth / 2,
          top: prodRect.top,
          left: prodRect.left,
          name: "lines",
          ...this.disabledObject,
        })
  
            this.c.add(imageLayers)
        this.c.add(prodRect)
        this.c.add(group)

            color && this.setBaseColor(color)

            this.c.renderAll()

            this.props.setSpinner(false)
      },
      null,
      {crossOrigin: "Anonymous"},
    )
      })
    });
    return
  }
  // ========================== old func
  setBaseColor = color => {
    if(!color){
      return
    }
    this.baseColor = color
    let prod = this.c.getObjects().find(item => item.name === "product")
    let filter = new fabric.Image.filters.BlendColor({
      color: color,
      mode: "multiply",
    })

    console.log(prod)

    if (prod && prod._objects && prod._objects.length) {
      const garment = prod._objects.find(obj => obj.name === "garment");
      if (garment) {
        garment.filters = [filter]
        garment.applyFilters()
        this.c.requestRenderAll()
      }
    } else {
    if (prod) {
      prod.filters = [filter]
      prod.applyFilters()
      this.c.requestRenderAll()
    }
  }
  }

  
  /**
   * add image to scene
   * @param {string} url - base64 image
   * @method
   */
  addImage = url => {
    // console.log(url)
    this.props.setSpinner(true)
    fabric.Image.fromURL(url, img => {
      let a = 1
      let prodBorder = this.c
        .getObjects()
        .find(item => item.name === "product-border")

      let scalePercent = [
        prodBorder.width / img.width,
        prodBorder.height / img.height,
      ].sort((a, b) => a - b)[0]

      if (scalePercent < 1) {
        a = scalePercent
        img.scale(scalePercent)
      }

      img.set({
        id: this._crtId(),
        top: prodBorder.top + prodBorder.height / 2 - (img.height * a) / 2,
        left: prodBorder.left + prodBorder.width / 2 - (img.width * a) / 2,
        fill: "transparent",
        clipPath: this._crtClip(prodBorder),
      })

      img.help = {}

      img.help.left = this.c.width / 2 - img.left
      img.help.top = this.c.height / 2 - img.top

      this.c.add(img)
      this.c.bringToFront(
        this.c.getObjects().find(item => item.name === "lines"),
      )
      this.c.renderAll()

      this.canvasContent
        .find(i => i.name === this.c.stateName)
        .payload.push(img.id)

      this.help.addToCartDisabled([...this.canvasContent])

      this.props.setSpinner(false)
    })
  }

  // ==========================START work with text
  /**
   * add text to scene
   * @param {string} text - text string from ui component
   * @method
   */
  addText = text => {
    this.props.setSpinner(true)
    let t = new fabric.Text(text)
    let prodBorder = this.c
      .getObjects()
      .find(item => item.name === "product-border")

    t.set({
      fill: "#000000",
      fontFamily: "Times New Roman",
      angle: 0,
      fontSize: 30,
      charSpacing: 30,
      id: this._crtId(),
    })

    t.set({
      top: prodBorder.top + prodBorder.height / 2 - t.height / 2,
      left: prodBorder.left + prodBorder.width / 2 - t.width / 2,
    })

    t.help = {}
    t.help.left = this.c.width / 2 - t.left
    t.help.top = this.c.height / 2 - t.top

    this.c.setActiveObject(t)
    this.c.add(t)

    t.clipPath = this._crtClip(prodBorder)

    this.c.bringToFront(this.c.getObjects().find(item => item.name === "lines"))

    this.c.renderAll()
    this.canvasContent.find(i => i.name === this.c.stateName).payload.push(t.id)
    this.help.addToCartDisabled([...this.canvasContent])

    this.props.setSpinner(false)
  }

  /**
   * create image from text
   * @param {object} text - fabricjs text object
   * @returns {string} textIMG - base64 image
   * @method
   */
  _createTextImg = text => {
    this.c.getObjects().forEach(item => (item.visible = false))

    let cachClipPath = text.clipPath
    let cachRotate = text.angle
    let cachZoom = this.c.getZoom()

    this._zoom(1)

    text.visible = true
    text.angle = 0
    text.clipPath = null

    let textIMG = this.c.toDataURL({
      top: text.top,
      left: text.left,
      width: text.width * text.scaleX,
      height: text.height * text.scaleY,
    })

    text.angle = cachRotate
    text.clipPath = cachClipPath
    this._zoom(cachZoom)

    this.c.getObjects().forEach(item => (item.visible = true))
    // console.log("TEXT_IMG", textIMG)
    // this.c.renderAll()
    return textIMG
  }

  setTextColor = (obj, color) => {
    obj.fill = color
    this.c.renderAll()
  }

  setTextAlign = (obj, align) => {
    obj.textAlign = align
    this.c.renderAll()
  }

  setTextFont = (obj, font) => {
    obj.fontFamily = font
    this.c.renderAll()
  }

  setFontSize = (obj, size) => {
    obj.fontSize = size
    this.c.renderAll()
  }
  setFontSpacing = (obj, spacing) => {
    obj.charSpacing = spacing
    this.c.renderAll()
  }

  setFontOutline = (obj, outline) => {
    obj.stroke = outline.color
    obj.strokeWidth = outline.weight
    this.c.renderAll()
  }

  updateText = (obj, newText) => {
    obj.type === "text" && obj.set("text", newText)
    this.c.renderAll()
  }

  setDiametr = (obj, arc) => {
    console.log(obj, arc)
  }

  // ==========================END work with text

  /**
   * remove objects from scene
   * @method
   */
  removeObjects = () => {
    this.c.getActiveObjects().forEach(item => {
      this.canvasContent.find(
        i => i.name === this.c.stateName,
      ).payload = this.canvasContent
        .find(i => i.name === this.c.stateName)
        .payload.filter(i => i !== item.id)

      this.help.addToCartDisabled([...this.canvasContent])

      this.c.remove(item)
    })
    this.c.discardActiveObject()
    this.c.renderAll()
  }

  /**
   * copy object
   * @param {object} obj - fabricjs object
   * @method
   */
  copyObject = obj => {
    if (this.c.getActiveObjects().length > 1) return
    this.props.setSpinner(true)
    let prodBorder = this.c
      .getObjects()
      .find(item => item.name === "product-border")

    obj.clone(cloned => {
      this.c.discardActiveObject()
      cloned.set({
        top: cloned.top + 5,
        left: cloned.left + 5,
        id: this._crtId(),
        clipPath: this._crtClip(prodBorder),
      })
      cloned.help = {}

      cloned.help.left = this.c.width / 2 - cloned.left
      cloned.help.top = this.c.height / 2 - cloned.top

      cloned.setCoords()

      this.c.add(cloned)
      this.props.setSpinner(false)
    })
  }

  flipYObjects = () => {
    this.c.getActiveObjects().forEach(item => item.set({flipY: !item.flipY}))
    this.c.renderAll()
  }

  flipXObjects = () => {
    this.c.getActiveObjects().forEach(item => item.set({flipX: !item.flipX}))
    this.c.renderAll()
  }

  bringToFrontObjs = () => {
    this.c.getActiveObjects().forEach(item => this.c.bringToFront(item))
    this.c.bringToFront(this.c.getObjects().find(item => item.name === "lines"))
    this.c.renderAll()
  }

  sendToBackObjs = () => {
    this.c.getActiveObjects().forEach(item => this.c.sendToBack(item))
    this.c.sendToBack(this.c.getObjects().find(item => item.name === "product"))

    this.c.renderAll()
  }

  /**
   * align object
   * @param {string} type - type from ui component
   * @method
   */
  alignObjects = type => {
    let objs = this.c.getActiveObjects()

    if (objs.length === 1) {
      let border = this.c
        .getObjects()
        .find(item => item.name === "product-border")

      switch (type) {
        case "LEFT":
          objs[0].set({
            left: border.left,
          })
          break
        case "RIGHT":
          objs[0].set({
            left: border.left + border.width - objs[0].width * objs[0].scaleX,
          })
          break
        case "CENTER":
          objs[0].set({
            left:
              border.left +
              border.width / 2 -
              (objs[0].width / 2) * objs[0].scaleX,
          })
          break
        case "TOP":
          objs[0].set({
            top: border.top,
          })
          break
        case "BOTTOM":
          objs[0].set({
            top: border.top + border.height - objs[0].height * objs[0].scaleY,
          })
          break
        default:
          break
      }

      objs[0].help.left = this.c.width / 2 - objs[0].left
      objs[0].help.top = this.c.height / 2 - objs[0].top
      objs[0].setCoords()
    }

    if (objs.length > 1) {
      switch (type) {
        case "LEFT": {
          let leftCoords = objs.map(item => item.left)
          let min = Math.min(...leftCoords)
          objs.forEach(item => (item.left = min))

          break
        }

        case "RIGHT": {
          let rightCoords = objs.map(
            item => item.left + item.width * item.scaleX,
          )
          let max = Math.max(...rightCoords)
          objs.forEach(item => (item.left = max - item.width * item.scaleX))

          break
        }

        case "CENTER": {
          let rightCoords = objs.map(
            item => item.left + item.width * item.scaleX,
          )
          let leftCoords = objs.map(item => item.left)

          let min = Math.min(...leftCoords)
          let max = Math.max(...rightCoords)

          let c = (max + min) / 2

          objs.forEach(item => (item.left = c - (item.width / 2) * item.scaleX))
          break
        }

        case "TOP": {
          let topCoords = objs.map(item => item.top)
          let min = Math.min(...topCoords)

          objs.forEach(item => (item.top = min))
          break
        }

        case "BOTTOM": {
          let bottomCoords = objs.map(
            item => item.top + item.height * item.scaleY,
          )
          let max = Math.max(...bottomCoords)

          objs.forEach(item => (item.top = max - item.height * item.scaleY))
          break
        }

        default:
          break
      }
    }

    this.c.renderAll()
  }

  /**
   * repeat object with paterrn
   * @param {object} obj - fabricjs object
   *  @method
   */
  repeatObject = obj => {
    if (obj) {
      this.props.setSpinner(true)
      // console.log(obj)
      let border = this.c
        .getObjects()
        .find(item => item.name === "product-border")

      const getUrl = () =>
        new Promise(resolve => {
          obj.angle_cash = obj.angle
          obj.clip_cash = obj.clipPath

          obj.angle = 0
          obj.clipPath = null

          this._zoom(1)

          this.c
            .getObjects()
            .filter(item => item.id !== obj.id)
            .forEach(item => (item.visible = false))

          this.c.renderAll()

          let url = this.c.toDataURL({
            left: obj.left,
            top: obj.top,
            width: obj.width * obj.scaleX,
            height: obj.height * obj.scaleY,
          })

          resolve(url)
        })

      getUrl().then(url => {
        fabric.util.loadImage(url, img => {
          this.removeObjects([obj]) //123123123123123
          this._zoom(this.c.getObjects().find(i => i.name === "product").zoom)

          this.c
            .getObjects()
            .filter(item => item.id !== obj.id)
            .forEach(item => (item.visible = true))

          let id = this._crtId()

          var rect = new fabric.Rect({
            id,
            width: border.width * 2,
            height: border.height * 2,
            // left: obj.left + (obj.width * obj.scaleX) / 2 - border.width,
            // top: obj.top + (obj.height * obj.scaleY) / 2 - border.height,
            cach_img: img,
            clipPath: this._crtClip(border),
            angle: obj.angle,
          })

          rect.help = {}
          rect.help.left = this.c.width / 2 - rect.left
          rect.help.top = this.c.height / 2 - rect.top

          this.c.add(rect)

          rect.set(
            "fill",
            new fabric.Pattern({
              source: img,
              repeat: "repeat",
            }),
          )

          this.canvasContent
            .find(i => i.name === this.c.stateName)
            .payload.push(id)
          this.help.addToCartDisabled([...this.canvasContent])

          this.c.centerObject(rect)
          rect.setCoords()

          this.props.setSpinner(false)
        })
      })
    }
  }

  getSelectedObject = () => this.c.getActiveObject()

  getSelectedImg = () =>
    this.c.getActiveObject() && this.c.getActiveObject().type === "image"
      ? this.c.getActiveObject()
      : null

  getSelectedText = () =>
    this.c.getActiveObject() && this.c.getActiveObject().type === "text"
      ? this.c.getActiveObject()
      : null

  getActObjs = () => this.c.getActiveObjects()

  discradSelected = () => {
    this.c.discardActiveObject()
    this.c.requestRenderAll()
  }

  scaleObject = (obj, scale) => {
    if (obj) {
      obj.scale(parseInt(scale) / 100)
      obj.setCoords()

      this.c.requestRenderAll()
    }
  }

  rotateObject = (obj, angle) => {
    obj.rotate(parseInt(angle))
    obj.setCoords()
    this.c.requestRenderAll()
  }

  /**
   * create json with canvas state, for work with several states
   *  @method
   */
  getCanvasState = () => {
    this.c.discardActiveObject()

    let cntObjs = this.c.getObjects().filter(item => item.id)

    cntObjs.forEach(item => {
      item.help.left = this.c.width / 2 - item.left
      item.help.top = this.c.height / 2 - item.top
    })
    return JSON.stringify(
      this.c.toJSON(["help", "name", "id", "cach_img", "stateName", "zoom"]),
    )
  }

  getAllUniqImages = () => {
    let objs = this.c
      .getObjects()
      .filter(item => item.id && item.type === "image")
      .map(item => item._element.src)
      .reduce(
        (acc, item) => {
          if (!acc.k.includes(item)) {
            acc.k.push(item)
            acc.res.push(item)
            return acc
          } else return acc
        },
        {k: [], res: []},
      ).res

    return objs
  }
  /**
   * load canvas state from json
   *  @param {string} state - json
   *  @method
   */
  loadCanvasState = state => {
    this.props.setSpinner(true)
   
    this.c.loadFromJSON(state, () => {
      let r = this.c.getObjects().filter(item => item.cash_img)

      r.forEach(item => {
        item.set({
          fill: new fabric.Pattern({
            source: item.cash_img,
            repeat: "repeat",
          }),
        })
      })

      this._calcPosition()
      this.baseColor && this.setBaseColor(this.baseColor)
      
      let prod = this.c.getObjects().find(i => i.name === "product")
      prod && this._zoom(prod.zoom)

      this.c.renderAll()
      this.props.setSpinner(false)
    })
  }

  clearCanvas = () => this.c.clear()

  _zoom = opt => {
    this.c.setViewportTransform(this._defVpTransform)
    this.c.zoomToPoint({x: this.c.width / 2, y: this.c.height / 2}, opt)
  }

  canvasResize = (width, height) => {
    // console.log("resize")
    this.c.setWidth(width)
    this.c.setHeight(height)

    this._calcPosition()
    this.c.calcOffset()
    this.c.renderAll()
  }

  _calcPosition = () => {
    // this.c.discardActiveObject()
    let objs = this.c.getObjects()
    let prod = objs.find(item => item.name === "product")

    let prodBorder = objs.find(item => item.name === "product-border")
    let prodLines = objs.find(item => item.name === "lines")
    let cntObjs = objs.filter(item => item.id)

    if (prod) {
      this._zoom(prod.zoom)

      prod.set({...this.disabledObject})
      setTimeout(() => this.c.centerObject(prod))
    }

    // setTimeout(() => {
    if (prodBorder) {
      prodBorder.set({
        left: this.c.width / 2 - prodBorder.help.left,
        top: this.c.height / 2 - prodBorder.help.top,
        ...this.disabledObject,
      })
    }

    if (prodLines) {
      prodLines.set({
        top: prodBorder.top + prodBorder.strokeWidth / 2,
        left: prodBorder.left + prodBorder.strokeWidth / 2,
        ...this.disabledObject,
      })
    }
    // })
    if (cntObjs.length) {
      cntObjs.forEach(item => {
        item.clipPath &&
          item.clipPath.set({
            left: prodBorder.left + prodBorder.strokeWidth / 2,
            top: prodBorder.top + prodBorder.strokeWidth / 2,
          })
        item.set({
          left: this.c.width / 2 - item.help.left,
          top: this.c.height / 2 - item.help.top,
        })
      })
    }

    objs.forEach(item => item.setCoords())

    this.c.renderAll()
  }

  /**
   * add to cart function / create zip / save all elements & create final json
   *  @param {string} state - json
   *  @method
   */
  addToCart = (state, callback, data = {}) => {
    this.c.discardActiveObject()
    let {key} = this.props
    let s = state.filter(item => item.name !== this.c.stateName && item.state)

    let m = this.c.getObjects().find(item => item.name === "product-border")
    let map
    this.firstState = this.getCanvasState()
    if (m) {
      map = {
        width: m.width,
        height: m.height,
        top: m.top,
        left: m.left,
      }
    }

    const getPayload = () => {
      let objs = this.c.getObjects()
      let conts = objs.filter(item => item.id)
      let imgs = conts.filter(item => item.type === "image" || item.cach_img)
      let text = conts.filter(item => item.type === "text")

      const getRepeatRealSize = img => {
        let o = {}
        if (img) {
          o = {
            repeat: true,
            rsize: {
              width: img.width,
              height: img.height,
              scaleX: img.scaleX,
              scaleY: img.scaleY,
              flipX: img.flipX,
              flipY: img.flipY,
            },
          }
        }
        return o
      }

      if (imgs.length) {
        imgs = imgs.map(item => ({
          width: item.width,
          height: item.height,
          top: item.top - m.top,
          left: item.left - m.left,
          angle: item.angle,
          scaleX: item.scaleX,
          scaleY: item.scaleY,
          flipX: item.flipX,
          flipY: item.flipY,
          img: item._element ? item._element.src : item.cach_img.src,
          ...getRepeatRealSize(item.cach_img),
        }))
      }

      if (text.length) {
        text = text.map(item => ({
          text: item.text,
          color: item.fill,
          fontSize: item.fontSize,
          fontFamily: item.fontFamily,
          strokeWidth: item.strokeWidth,
          stroke: item.stroke,
          flipX: item.flipX,
          flipY: item.flipY,
          width: item.width,
          height: item.height,
          top: item.top - m.top,
          left: item.left - m.left,
          angle: item.angle,
          scaleX: item.scaleX,
          scaleY: item.scaleY,
          img_path: `imgs/text-${(Math.random() * 100000).toFixed()}.png`,
          img_src: this._createTextImg(item),
        }))
      }
      return {imgs, text}
    }

    const getSideContent = () => {
      let name = this.c.stateName
      let ch_zoom = this.c.getZoom()
      this._zoom(1)

      let prod = this.c.getObjects().find(i => i.name === "product")
      let border = this.c.getObjects().find(i => i.name === "product-border")
      border.visible = false
      let screenShot = this.c.toDataURL({
        // crossOrigin: "anonymous",
        format: "jpeg",
        quality: 0.2,
        multiplier: 1,

        left: prod.left,
        top: prod.top,
        width: prod.width,
        height: prod.height,
      })

      let boxCmSize = this.canvasConfigs.find(item => item.added_name === name)
      const dpi = 150
      const cmInInch = 2.54

      let mustScaleX = ((boxCmSize.width / cmInInch) * dpi) / border.width
      let mustScaleY = ((boxCmSize.height / cmInInch) * dpi) / border.height
      if (mustScaleX>mustScaleY) mustScaleY = mustScaleX; else mustScaleX=mustScaleY;
      // let maxScaleFactor = Math.max(mustScaleX, mustScaleY)
      console.log(mustScaleX, mustScaleY)

      border.cash_scaleX = border.scaleX
      border.cash_scaleY = border.scaleY

      border.scaleX = border.scaleX * mustScaleX
      border.scaleY = border.scaleY * mustScaleY

      border.visible = false
      prod.visible = false

      this.c
        .getObjects()
        .filter(i => i.id)
        .forEach(i => {
          i.cach_data = {
            scale_x: i.scaleX,
            scale_y: i.scaleY,
            left: i.left,
            top: i.top,
            clip: i.clipPath,
          }

          i.left =
            border.left +
            border.strokeWidth / 2 +
            (i.left - border.left) * mustScaleX
          i.top =
            border.top +
            border.strokeWidth / 2 +
            (i.top - border.top) * mustScaleY

          i.scaleX = border.scaleX * i.scaleX
          i.scaleY = border.scaleY * i.scaleY

          i.clipPath = null
        })

      let box = this.c.toDataURL({
        left: border.left,
        top: border.top,

        width: (border.width + border.strokeWidth / 2) * border.scaleX,
        height: (border.height + border.strokeWidth / 2) * border.scaleY,
      })

      this.c
        .getObjects()
        .filter(i => i.id)
        .forEach(i => {
          i.left = i.cach_data.left
          i.top = i.cach_data.top

          i.scaleX = i.cach_data.scale_x
          i.scaleY = i.cach_data.scale_y

          i.clipPath = i.cach_data.clip
        })

      prod.visible = true
      border.visible = true

      let box150dpi = changeDpiDataUrl(box, dpi)

      this._zoom(ch_zoom)
      return {name, box: box150dpi, screenShot, ...getPayload()}
    }

    const getAllSides = () => {
      return new Promise(resolve => {
        let arr = s.map(item => {
          return new Promise(resolveArr => {
            this.clearCanvas()
            this.c.loadFromJSON(item.state, () => {
              let r = this.c.getObjects().filter(item => item.cash_img)
              r.forEach(item => {
                item.set({
                  fill: new fabric.Pattern({
                    source: item.cash_img,
                    repeat: "repeat",
                  }),
                })
              })
              this._calcPosition()
              this.c.renderAll()

              // final = {...final, side: [...final.side, getSideContent()]}
              resolveArr(getSideContent())
            })
          })
        })

        Promise.all(arr).then(res => {
          this.clearCanvas()
          this.loadCanvasState(this.firstState)
          resolve([...res, firstState])
        })
      })
    }

    const zip = new JSZip()
    let firstState = getSideContent()

    const crtZip = final => {
      let imgs = final.side
        .reduce((acc, item) => {
          acc = [...acc, ...item.imgs]
          return acc
        }, [])
        .reduce(
          (acc, item) => {
            if (!acc.k.find(i => i.img === item.img)) {
              acc.k.push(item)
              acc.res.push(item)
              return acc
            } else return acc
          },
          {k: [], res: []},
        )
        .res.map(item => ({
          img_Path: `imgs/image-${(Math.random() * 100000).toFixed()}.${item.img.split(",")[0].split(";")[0].split("/")[1]
          }`,
          img: item.img,
        }))

      final.side.forEach(item => {
        item.imgs.forEach(i => {
          let o = imgs.find(it => it.img === i.img)
          if (o) {
            i.img = o.img_Path
          }
        })

        let boxpath = `imgs/box-${(Math.random() * 100000).toFixed()}.png`
        zip.file(boxpath, item.box.split(",")[1], {
          base64: true,
        })

        item.box = boxpath

        item.text.forEach(i => {
          zip.file(`${i.img_path}`, i.img_src.split(",")[1], {
            base64: true,
          })
        })

        zip.file(`${item.name}.jpeg`, item.screenShot.split(",")[1], {
          base64: true,
        })

        // item.screenShot = `${item.name}.jpeg`
        item.screenShotName = `${item.name}.jpeg`
      })

      imgs.forEach(item => {
        zip.file(item.img_Path, item.img.split(",")[1], {
          base64: true,
        })
      })
      // console.log(zip)
      let newFinalDoc = {
        sku: data.sku,
        parts: [...final.side].map(i => ({
          box: i.box,
          name: i.name,
          screenShot: i.screenShotName,
          id: this.canvasConfigs.find(item => item.added_name === i.name)
            .object_id,
          color: "",
          positionInfo: {
            dx: this.canvasConfigs.find(item => item.added_name === i.name).dx,
            dy: this.canvasConfigs.find(item => item.added_name === i.name).dy,

            hNetInUOM: this.canvasConfigs.find(
              item => item.added_name === i.name,
            ).hNetInUOM,
            wNetInUOM: this.canvasConfigs.find(
              item => item.added_name === i.name,
            ).wNetInUOM,
            height: this.canvasConfigs.find(item => item.added_name === i.name)
              .height,
            width: this.canvasConfigs.find(item => item.added_name === i.name)
              .width,
          },
          logos: [
            ...i.imgs,
            ...i.text.map(item => {
              delete item.img_src
              return {...item}
            }),
          ],
        })),
      }

      // console.log(this.canvasConfigs)
      // console.log("==FINAL==", newFinalDoc)
      // console.log("==CONFIGS==", final)
      const KEY = process.env.REACT_APP_KEY
      const IV = process.env.REACT_APP_IV

      let encJson = CRYPTO_JS.AES.encrypt(
        JSON.stringify(newFinalDoc),
        CRYPTO_JS.enc.Utf8.parse(KEY),
        {
          iv: CRYPTO_JS.enc.Utf8.parse(IV),
          padding: CRYPTO_JS.pad.Pkcs7,
          mode: CRYPTO_JS.mode.CBC,
        },
      )

      zip.file("info.json", encJson.toString())
    }

    const saveZip = final => {
      zip.generateAsync({type: "blob"}).then(content => {
        var files = new File([content], "customizer.zip")
        var form_data = new FormData()
        form_data.append("file", files)
        let file_name = uuidv4().split("-").join("")
        console.log("ZIP_NAME:", file_name)

        storage
          .ref()
          .child(`${key}/dtg_storage/${file_name}.zip`)
          .put(files)
          .then(res => {
            callback({final, metadata: res.metadata})
          })
          .catch(err => console.log(err))
      })
    }

    getAllSides().then(sidesContent => {
      let final = {
        map: map,
        side: sidesContent,
      }

      crtZip(final)
      saveZip(final)
    })
  }
}

export default Viewer2d
